'use client'

import {FC} from 'react'

import {BreadcrumbStep} from '@/app/lib/structuredData'
import {ClassAssistItemOverview} from '@/components/class-assist/item/ClassAssistItemOverview'
import {GetStartedWithVerificationBanner} from '@/components/common/banner/GetStartedWithVerificationBanner'
import {Breadcrumbs} from '@/components/common/content/Breadcrumbs'
import {Section} from '@/components/common/content/Section'
import {Hero} from '@/components/common/hero/Hero'
import {MostRecentArticles} from '@/components/common/recent-articles/MostRecentArticles'
import {Tile} from '@/components/common/tiles/Tile'
import {Page} from '@/components/Page'
import {ServiceRequestTiles} from '@/components/service-request/ServiceRequestTiles'
import {getClassAssistContent, getClassAssistItemContent} from '@/content/classAssist'
import {useTranslation} from '@/i18n'
import {ClassAssistItemOverviewData} from '@/utils/class-assist/types'
import {isPartiallySupportedLocale} from '@/utils/constants'
import {RecentArticleCard} from '@/utils/contentful'
import {useLanguage} from '@/utils/i18n/language'
import {analytics} from '@/utils/tracking/analytics'

type ClassAssistItemPageProps = {
  breadcrumbTrail: BreadcrumbStep[]
  classCode: string
  item: ClassAssistItemOverviewData
  relatedArticles: RecentArticleCard[]
}

const ClassAssistItemPage: FC<ClassAssistItemPageProps> = ({
  breadcrumbTrail,
  classCode,
  item,
  relatedArticles,
}) => {
  const language = useLanguage()
  const {t} = useTranslation()

  const {hero} = getClassAssistItemContent()
  const {headerLabel, header, tiles, relatedArticlesTitle, getStartedBanner} =
    getClassAssistContent(language)

  const isPartiallySupported = isPartiallySupportedLocale(language)

  const itemName = item.name

  return (
    <>
      <Page
        navigationLabel={isPartiallySupported ? t(headerLabel) : undefined}
        hasGetHelpButton={!isPartiallySupported}
        hasRegionalSettings={!isPartiallySupported}
        hasLogin={!isPartiallySupported}
        mainCtaButton={{
          text: t(header.buttonLinkText),
          linkHref: header.buttonLinkHref,
          onClick: () =>
            analytics.track('Click on Start registration (Class assist - Header)', {
              classCode,
              item: itemName,
            }),
        }}
      >
        <Hero
          title={t(hero.title, {itemName, classCode})}
          subtitle={t(hero.subtitle, {itemName, classCode})}
          breadcrumbs={<Breadcrumbs trail={breadcrumbTrail} hideCurrent />}
          maxWidth="100%"
        />
        <Section hasPaddingTop>
          <ClassAssistItemOverview item={item} classCode={classCode} />
        </Section>
        {relatedArticles.length > 0 && (
          <Section hasPaddingTop>
            <MostRecentArticles
              articlesTitle={t(relatedArticlesTitle)}
              articles={relatedArticles}
              trackedEvent={{
                name: 'RelatedBlogs-ClassAssistItemOverview',
                parameters: {classCode, item: itemName},
              }}
            />
          </Section>
        )}
        <Section hasPaddingTop>
          <ServiceRequestTiles title={t(tiles.title)}>
            {tiles.list.map(({title, description, imageSrc, imageAlt}, i) => (
              <Tile
                key={i}
                title={t(title)}
                description={t(description)}
                imageSrc={imageSrc}
                imageAlt={imageAlt}
              />
            ))}
          </ServiceRequestTiles>
        </Section>
        <GetStartedWithVerificationBanner
          title={t(getStartedBanner.title)}
          primaryButtonLink={getStartedBanner.buttonLink}
          primaryButtonText={t(getStartedBanner.buttonText)}
          primaryButtonTrackedEvent="GetStartedBanner-ClassAssistItemPage"
          hasPaddingTop
        />
      </Page>
    </>
  )
}

export default ClassAssistItemPage
